import {
    Box, Button,
    Container,
    Divider,
    IconButton,
    ImageList,
    ImageListItem, Modal, Paper,
    Stack, Tooltip,
    Typography, useMediaQuery
} from "@mui/material";
import {redirect, useLoaderData} from "react-router-dom";
import {BoldHeadingTypo} from "./book";
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import EventNoteIcon from '@mui/icons-material/EventNote';
import React, {useEffect, useMemo, useState} from "react";
import BoxButton from "../../components/BoxButton";
import CollectionsOutlinedIcon from "@mui/icons-material/CollectionsOutlined";
import PlaceholderedImage from "../../components/placeholderImage";
import ImageModal from "../../components/imageModal";
import {CloseOutlined} from "@mui/icons-material";
import {isNonEmptyObject} from "./object/object_details";
import {differenceInSeconds, parseISO, format, differenceInCalendarDays} from "date-fns";
import api from "../../hooks/useApi";
import {showSnackbar} from "../../components/snackbarUtils";
import AuthProvider from "../../hooks/authProvider";
import {Accordion, AccordionSummary, AccordionDetails} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PageTitle from "../../components/PageTitle";

export async function loader({request, params}) {
    const id = params.id;
    const accessToken = localStorage.getItem('access_token');

    const response = await api.get(`/api/booking_modules/use/booking/form/${id}/`, {
        headers: {
            Authorization: `Bearer ${accessToken}`
        }
    });

    const book = response.data.data;
    return {book};
}

export default function BookDetails() {
    const {book} = useLoaderData();

    const ft = useMemo(() => {
        const st = parseISO(book.booking_from);
        const et = parseISO(book.booking_to);
        return {
            st: format(st, 'dd.MM.yyyy'),
            et: format(et, 'dd.MM.yyyy 11:00'),
            ds: differenceInCalendarDays(et, st),
        };
    }, [book.booking_from, book.booking_to]);

    const [modalCfg, setModalCfg] = useState({
        open: false,
        data: {},
        mode: null
    });

    const isMobile = useMediaQuery("(max-width: 600px)");

    const [imageSrc, setImageSrc] = useState(null);
    const [currentImageIndex, setCurrentImageIndex] = useState(null);
    const handleNextImage = () => {
        setCurrentImageIndex((prevIndex) => {
            if (!modalCfg.data.hotel_image_for_booking_details) return 0; // Проверяем, есть ли изображения
            const nextIndex = prevIndex + 1 < modalCfg.data.hotel_image_for_booking_details.length ? prevIndex + 1 : 0;
            return nextIndex;
        });
    };

    const handlePrevImage = () => {
        setCurrentImageIndex((prevIndex) => {
            if (!modalCfg.data.hotel_image_for_booking_details) return 0;
            const prevIndexNew = prevIndex - 1 >= 0 ? prevIndex - 1 : modalCfg.data.hotel_image_for_booking_details.length - 1;
            return prevIndexNew;
        });
    };

    const maxMobileImages = 20;

    return (
        <>
            <PageTitle title={`Деталі бронювання: ${book?.id || "ID"}`} />
            <Container maxWidth='lg' sx={{mb: 10}}>
                <Box sx={{my: 2, mb: 3, width: '100%', textAlign: 'center'}}>
                    <Typography variant='h5' fontWeight='bold'>Інформація про бронювання</Typography>
                    <Typography variant='h6'>{book.first_name} {book.last_name}, дякуємо за бронювання!</Typography>
                </Box>
                <Box>
                    <Stack direction='column' spacing={2}>
                        <CancellationTimer createdAt={book.created_at}/>
                        <Box sx={{display: "flex", alignItems: "center", gap: 1, mt: 1}}>
                            <Typography variant="h6" sx={{fontWeight: "bold", color: getStatusColor(book.status)}}>
                                Статус: {getStatusLabel(book.status)}
                            </Typography>
                        </Box>
                        <BoldHeadingTypo>{book.hotel?.classification?.name}</BoldHeadingTypo>
                        <BoldHeadingTypo>{book.hotel?.name}</BoldHeadingTypo>
                        <Typography sx={{alignItems: 'center', display: 'flex', mt: 2}}>
                            <PlaceOutlinedIcon sx={{color: 'blue'}}
                                               fontSize='large'/>{book.hotel?.location?.city.city}, {book.hotel?.location?.text_address}
                        </Typography>
                        <Box
                            sx={{
                                width: isMobile ? "100%" : "500px",
                                aspectRatio: isMobile ? "4 / 3" : "1 / 1", // мобильное 4:3, десктоп квадрат
                                overflow: "hidden",
                                borderRadius: 2
                            }}
                        >
                            {book?.hotel?.location?.google_map ? (
                                <Box
                                    sx={{width: "100%", height: "100%"}}
                                    dangerouslySetInnerHTML={{__html: book.hotel.location.google_map}}
                                />
                            ) : (
                                <Typography variant="body2" color="textSecondary">
                                    Данные недоступны
                                </Typography>
                            )}
                        </Box>

                        <Divider/>
                        <BoldHeadingTypo>Вартість</BoldHeadingTypo>
                        <BoldHeadingTypo variant='h6'>{book.price}</BoldHeadingTypo>
                        <Divider/>
                        <BoldHeadingTypo>Деталі бронювання</BoldHeadingTypo>
                        <Box sx={{width: "100%"}}>
                            <div style={{display: "flex", flexDirection: "column", alignItems: "center"}}>
                                {/* Заголовки */}
                                <div
                                    style={{
                                        display: "grid",
                                        gridTemplateColumns: isMobile ? "1fr" : "repeat(4, 1fr)",
                                        alignItems: "center",
                                        width: "100%",
                                        maxWidth: "600px",
                                        borderBottom: "1px solid #ccc",
                                        paddingBottom: "8px",
                                        marginBottom: "8px",
                                        textAlign: isMobile ? "center" : "initial",
                                    }}
                                >
                                    <Typography align="center">Початок бронювання</Typography>
                                    <Typography align="center"
                                                sx={{display: "flex", alignItems: "center", justifyContent: "center"}}>
                                        <ExitToAppIcon/>
                                        <span style={{marginLeft: "4px"}}>Заїзд</span>
                                    </Typography>
                                    <Typography align="center"
                                                sx={{display: "flex", alignItems: "center", justifyContent: "center"}}>
                                        <ExitToAppIcon/>
                                        <span style={{marginLeft: "4px"}}>Виїзд</span>
                                    </Typography>
                                    <Typography align="center"
                                                sx={{display: "flex", alignItems: "center", justifyContent: "center"}}>
                                        <EventNoteIcon/>
                                        <span style={{marginLeft: "4px"}}>Тривалість проживання</span>
                                    </Typography>
                                </div>

                                {/* Данные */}
                                <div
                                    style={{
                                        display: "grid",
                                        gridTemplateColumns: isMobile ? "1fr" : "repeat(4, 1fr)",
                                        alignItems: "center",
                                        width: "100%",
                                        maxWidth: "600px",
                                        paddingTop: "8px",
                                        textAlign: isMobile ? "center" : "initial",
                                    }}
                                >
                                    <Typography align="center" sx={{fontWeight: "bold"}}>{ft.st}</Typography>
                                    <Typography align="center"
                                                sx={{fontWeight: "bold"}}>{book.time_arrived}</Typography>
                                    <Typography align="center" sx={{fontWeight: "bold"}}>{ft.et}</Typography>
                                    <Typography align="center" sx={{fontWeight: "bold"}}>{ft.ds + 1} днів
                                        ({ft.ds} ночей)</Typography>
                                </div>
                            </div>
                        </Box>
                        <Divider/>
                        <Box>
                            <Typography sx={{fontWeight: 'bold'}}>Коментар: </Typography>
                            <Typography>{book.comments}</Typography>
                        </Box>
                        <Divider/>
                        {book.hotel_children && book.hotel_children.length > 0 ? (
                            book.hotel_children.map((child, index) => (
                                <Accordion key={index} sx={{mt: 2, width: "100%"}}>
                                    <AccordionSummary expandIcon={<ExpandMoreIcon/>}>
                                        <Typography>Фото номеру {child.name}</Typography>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <Box sx={{
                                            display: "flex",
                                            overflowX: "auto",
                                            gap: 1,
                                            pb: 1,
                                            "&::-webkit-scrollbar": {display: "none"}
                                        }}>
                                            {child.hotel_image_for_booking_details.slice(0, maxMobileImages).map((photo, idx) => (
                                                <Box key={idx}
                                                     sx={{minWidth: "120px", height: "100px", cursor: "pointer"}}
                                                     onClick={() => {
                                                         setModalCfg({open: true, data: child, mode: 'photo'});
                                                         setCurrentImageIndex(idx);
                                                     }}>
                                                    <img
                                                        src={photo}
                                                        alt={`Фото ${idx + 1}`}
                                                        style={{
                                                            width: "100%",
                                                            height: "100%",
                                                            objectFit: "cover",
                                                            borderRadius: "8px",
                                                        }}
                                                    />
                                                </Box>
                                            ))}
                                            {/* Кнопка "Еще фото" */}
                                            {child.hotel_image_for_booking_details.length > maxMobileImages && (
                                                <Box
                                                    sx={{
                                                        minWidth: "120px",
                                                        height: "100px",
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                        backgroundColor: "rgba(0,0,0,0.5)",
                                                        color: "white",
                                                        fontWeight: "bold",
                                                        borderRadius: "8px",
                                                        cursor: "pointer",
                                                    }}
                                                    onClick={() => {
                                                        setModalCfg({open: true, data: child, mode: 'photo'});
                                                        setCurrentImageIndex(0);
                                                    }}
                                                >
                                                    +{child.hotel_image_for_booking_details.length - maxMobileImages} фото
                                                </Box>
                                            )}
                                        </Box>
                                    </AccordionDetails>
                                </Accordion>
                            ))
                        ) : (
                            <Accordion sx={{mt: 2, width: "100%"}}>
                                <AccordionSummary expandIcon={<ExpandMoreIcon/>}>
                                    <Typography>Фото готелю</Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <Box sx={{
                                        display: "flex",
                                        overflowX: "auto",
                                        gap: 1,
                                        pb: 1,
                                        "&::-webkit-scrollbar": {display: "none"}
                                    }}>
                                        {book.hotel.hotel_image_for_booking_details.slice(0, maxMobileImages).map((photo, index) => (
                                            <Box key={index}
                                                 sx={{minWidth: "120px", height: "100px", cursor: "pointer"}}
                                                 onClick={() => {
                                                     setModalCfg({open: true, data: book.hotel, mode: 'photo'});
                                                     setCurrentImageIndex(index);
                                                 }}>
                                                <img
                                                    src={photo}
                                                    alt={`Фото ${index + 1}`}
                                                    style={{
                                                        width: "100%",
                                                        height: "100%",
                                                        objectFit: "cover",
                                                        borderRadius: "8px",
                                                    }}
                                                />
                                            </Box>
                                        ))}
                                        {/* Кнопка "Еще фото" */}
                                        {book.hotel.hotel_image_for_booking_details.length > maxMobileImages && (
                                            <Box
                                                sx={{
                                                    minWidth: "120px",
                                                    height: "100px",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    backgroundColor: "rgba(0,0,0,0.5)",
                                                    color: "white",
                                                    fontWeight: "bold",
                                                    borderRadius: "8px",
                                                    cursor: "pointer",
                                                }}
                                                onClick={() => {
                                                    setModalCfg({open: true, data: book.hotel, mode: 'photo'});
                                                    setCurrentImageIndex(0);
                                                }}
                                            >
                                                +{book.hotel.hotel_image_for_booking_details.length - maxMobileImages} фото
                                            </Box>
                                        )}
                                    </Box>
                                </AccordionDetails>
                            </Accordion>
                        )}

                        {/* 🎨 Модальное окно с полной галереей */}
                        {modalCfg.open && modalCfg.mode === 'photo' && currentImageIndex !== null && (
                            <ImageModal
                                imageSrc={modalCfg.data.hotel_image_for_booking_details[currentImageIndex]}
                                setImageSrc={setImageSrc}
                                handleNextImage={handleNextImage}
                                handlePrevImage={handlePrevImage}
                                resetCurrentImageIndex={() => setCurrentImageIndex(null)}
                                imageSingleDescriptions={modalCfg.data.hotel_image_descriptions_all[currentImageIndex]}
                            />
                        )}
                        <Divider/>
                        {(book.hotel_children && book.hotel_children.length > 0) ? (
                            book.hotel_children.map((Obj, index1) => (
                                <div key={index1}>
                                    <Typography>{Obj.classification.name}</Typography>
                                    <Typography sx={{fontWeight: 'bold'}}> Тип ліжок:</Typography>
                                    {Obj.bedrooms?.map((bedroom, index2) => (
                                        <Typography key={`${index1}${index2}`}>
                                            {bedroom.type_beds} {bedroom.number_of_beds} спальних місць
                                        </Typography>
                                    ))}
                                </div>
                            ))
                        ) : (
                            <div>
                                <Typography>{book.hotel?.classification?.name}</Typography>
                                <Typography sx={{fontWeight: 'bold'}}> Тип ліжок:</Typography>
                                {book.hotel?.bedrooms?.map((bedroom, index) => (
                                    <Typography key={index}>
                                        {bedroom.type_beds} {bedroom.number_of_beds} спальних місць
                                    </Typography>
                                ))}
                            </div>
                        )}
                        <Divider/>
                        {book.hotel_children?.length === 1 ? (
                            <>
                                <BoldHeadingTypo>Передплата та скасування
                                    для {book.hotel_children?.[0].name}</BoldHeadingTypo>
                                <Typography>Інформація про передплату та скасування</Typography>
                                <Typography>
                                    <span style={{fontWeight: 'bold'}}>Політика скасування: </span>
                                    {book.hotel_children?.[0].accommodation_conditions?.cancellation_policy}
                                </Typography>
                                <Typography>
                                    <span style={{fontWeight: 'bold'}}>Передплата: </span>
                                    {book.hotel_children?.[0].accommodation_conditions?.prepayment ? "Так" : "Ні"}
                                </Typography>
                                <Typography>
                                    <span style={{fontWeight: 'bold'}}>Сума скасування: </span>
                                    {book.hotel_children?.[0].accommodation_conditions?.cancellation_summ}
                                </Typography>
                                <Typography>
                                    <span style={{fontWeight: 'bold'}}>Сума скасування при незаїзді: </span>
                                    {book.hotel_children?.[0].accommodation_conditions?.cancellation_summ_no_arrived}
                                </Typography>
                                <Divider/>
                            </>
                        ) : (
                            book.hotel_children?.map((child, index) => (
                                <div key={index}>
                                    <BoldHeadingTypo>Передплата та скасування для {child.name}</BoldHeadingTypo>
                                    <Typography>Інформація про передплату та скасування</Typography>
                                    <Typography>
                                        <span style={{fontWeight: 'bold'}}>Політика скасування: </span>
                                        {child.accommodation_conditions?.cancellation_policy || "Не вказано"}
                                    </Typography>
                                    <Typography>
                                        <span style={{fontWeight: 'bold'}}>Передплата: </span>
                                        {child.accommodation_conditions?.prepayment ? "Так" : "Ні"}
                                    </Typography>
                                    <Typography>
                                        <span style={{fontWeight: 'bold'}}>Сума скасування: </span>
                                        {child.accommodation_conditions?.cancellation_summ || "Не вказано"}
                                    </Typography>
                                    <Typography>
                                        <span style={{fontWeight: 'bold'}}>Сума скасування при незаїзді: </span>
                                        {child.accommodation_conditions?.cancellation_summ_no_arrived || "Не вказано"}
                                    </Typography>
                                    <Divider/>
                                </div>
                            ))
                        )}
                        <BoldHeadingTypo>Контактні дані для звʼязку</BoldHeadingTypo>
                        <Typography>{book.hotel.first_manager}</Typography>
                    </Stack>
                </Box>
            </Container>
            <RoomDetails modalCfg={modalCfg} setModalCfg={setModalCfg}/>
        </>
    )
}
const RoomDetails = ({modalCfg, setModalCfg}) => {
    const data = modalCfg.data[0] || modalCfg.data;
    const [imageSrc, setImageSrc] = useState(null);
    const [currentImageIndex, setCurrentImageIndex] = useState(null);

    const handleNextImage = () => {
        setCurrentImageIndex((prevIndex) => {
            const nextIndex = prevIndex + 1 < data.hotel_image_for_booking_details.length ? prevIndex + 1 : 0;
            return nextIndex;
        });
    };

    const handlePrevImage = () => {
        setCurrentImageIndex((prevIndex) => {
            const prevIndexNew = prevIndex - 1 >= 0 ? prevIndex - 1 : data.hotel_image_for_booking_details.length - 1;
            return prevIndexNew;
        });
    };

    const isMobile = useMediaQuery("(max-width: 600px)");

    const PhotoContent = () => {
        return (
            <Box sx={{width: "100%", maxHeight: "100%", overflowY: "auto"}}>
                <ImageList variant="masonry" cols={isMobile ? 2 : 3} gap={8}>
                    {data.hotel_image_for_booking_details?.length > 0 && data.hotel_image_for_booking_details.map((el, index) => (
                        <ImageListItem key={index}>
                            <PlaceholderedImage src={el} onClick={() => setCurrentImageIndex(index)}
                                                style={{
                                                    width: "100%",
                                                    maxHeight: "100%",
                                                    cursor: "pointer",
                                                    borderRadius: "15px"
                                                }}/>
                        </ImageListItem>
                    ))}
                </ImageList>
                {currentImageIndex !== null && (
                    <ImageModal
                        imageSrc={data.hotel_image_for_booking_details[currentImageIndex]}
                        setImageSrc={setImageSrc}
                        handleNextImage={handleNextImage}
                        handlePrevImage={handlePrevImage}
                        resetCurrentImageIndex={() => setCurrentImageIndex(null)}
                        imageSingleDescriptions={data.hotel_image_descriptions_all[currentImageIndex]}
                    />
                )}
            </Box>

        )
    }

    const renderContent = () => {
        switch (modalCfg.mode) {
            case 'photo':
                return (<PhotoContent/>)
            default:
                <div>Інформація відсутня</div>
        }
    }
    const getLabel = () => {
        switch (modalCfg.mode) {
            case 'photo':
                return 'Фотографії номеру'
            default:
                <div>Невідомо.</div>
        }
    }
    return (
        <Modal open={modalCfg.open}>
            <Container maxWidth='md'
                       sx={{height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                <Paper sx={{padding: '20px', maxHeight: '90vh', minHeight: '50vh', width: '100%', overflow: 'auto'}}>
                    <Box className='flex stickSides' sx={{alignItems: 'center'}}>
                        <Typography>{getLabel()} {' '}
                            <Typography component='span' sx={{fontWeight: 'bold'}}>
                                {data.name}
                            </Typography>
                        </Typography>
                        <Tooltip arrow placement='top' title='Закрити'>
                            <IconButton onClick={() => {
                                setModalCfg(prev => ({...prev, open: false, data: {}, mode: null}));
                            }}>
                                <CloseOutlined/>
                            </IconButton>
                        </Tooltip>
                    </Box>
                    <Box className='modalContentBooking' sx={{
                        "& .MuiBox-root": {
                            my: 2
                        }
                    }}>
                        {isNonEmptyObject(data) ? (
                            <>
                                {renderContent()}
                            </>
                        ) : (
                            <>Нічого не знайдено!</>
                        )}
                    </Box>
                </Paper>
            </Container>
        </Modal>
    )
}

const CancellationTimer = ({createdAt}) => {
    const {book} = useLoaderData();
    const [timeLeft, setTimeLeft] = useState(null);
    const [hasTimeExpired, setHasTimeExpired] = useState(false);

    useEffect(() => {
        if (!createdAt) return;

        const createdDate = parseISO(createdAt);
        const expirationDate = new Date(createdDate.getTime() + 24 * 60 * 60 * 1000); // 24 часа после created_at

        const updateTimer = () => {
            const now = new Date();
            const secondsLeft = differenceInSeconds(expirationDate, now);

            if (secondsLeft <= 0) {
                setHasTimeExpired(true);
                setTimeLeft(null);
                return;
            }

            const hours = Math.floor(secondsLeft / 3600);
            const minutes = Math.floor((secondsLeft % 3600) / 60);
            const seconds = secondsLeft % 60;

            setTimeLeft({hours, minutes, seconds});
        };

        updateTimer();
        const timerInterval = setInterval(updateTimer, 1000);

        return () => clearInterval(timerInterval);
    }, [createdAt]);

    const [loading, setLoading] = useState(false);

    const handleRequestCancellation = async (status) => {
        setLoading(true);
        try {
            await api.patch(`/api/booking_modules/use/booking/update-status/${book.id}/${status}/`, {status});
            showSnackbar("Запит успішно відправлено!", "success", {
                sx: {backgroundColor: "#4caf50", color: "white"},
            });
        } catch (error) {
            showSnackbar("Помилка під час відправки запиту!", "error", {
                sx: {backgroundColor: "#ff0000", color: "white"},
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box sx={{textAlign: "center", padding: 2}}>
            {hasTimeExpired ? (
                <>
                    <Typography variant="h6" sx={{color: "#ff4d4d", fontWeight: "bold"}}>
                        Час для безкоштовного скасування минув!
                    </Typography>
                    <Button
                        variant="contained"
                        color="error"
                        sx={{mt: 2}}
                        disabled={loading}
                        onClick={() => handleRequestCancellation(5)}
                    >
                        Запит скасування
                    </Button>
                </>
            ) : timeLeft ? (
                <>
                    <Typography variant="h6" sx={{color: "#1890ff", fontWeight: "bold"}}>
                        Час, що залишився для безкоштовного скасування:{" "}
                        <span style={{fontSize: "1.2em"}}>
                            {`${timeLeft.hours} г ${timeLeft.minutes} хв ${timeLeft.seconds} сек`}
                        </span>
                    </Typography>
                    <Button
                        variant="contained"
                        color="primary"
                        sx={{mt: 2}}
                        disabled={loading}
                        onClick={() => handleRequestCancellation(1)}
                    >
                        Запит на безкоштовне скасування
                    </Button>
                </>
            ) : (
                <Typography variant="h6">Завантаження...</Typography>
            )}
        </Box>
    );
};

const getStatusLabel = (status) => {
    const statusMap = {
        "1": "Запит на безкоштовне скасування",
        "2": "Бронювання скасовано",
        "3": "Відмова у безкоштовному скасуванні",
        "4": "Дійсне бронювання",
        "5": "Запит на скасування бронювання"
    };
    return statusMap[status] || "Невідомий статус";
};

const getStatusColor = (status) => {
    const colorMap = {
        "1": "#FFA500", // Оранжевый
        "2": "#FF4D4D", // Красный
        "3": "#808080", // Серый
        "4": "#4CAF50", // Зеленый
        "5": "#FF8C00"  // Темно-оранжевый
    };
    return colorMap[status] || "#000"; // По умолчанию черный
};
